import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";

type LookRow = {
  id: string;
  title: string | null;
  image_path: string;
  featured: boolean;
  created_at: string;
  creator_id: string;
  creators: { handle: string | null; display_name: string | null } | null;
};

export const Route = createFileRoute("/_authenticated/admin/looks")({
  head: () => ({
    meta: [
      { title: "Looks — Admin" },
      { name: "description", content: "Curate which shop-the-look posts are featured across Pluggz." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LooksAdmin,
});

import { lookImageUrl as publicUrl } from "@/lib/storage";

function LooksAdmin() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<"all" | "featured">("all");

  const list = useQuery({
    queryKey: ["admin-looks", filter],
    queryFn: async () => {
      let q = supabase
        .from("looks")
        .select("id,title,image_path,featured,created_at,creator_id,creators(handle,display_name)")
        .order("created_at", { ascending: false })
        .limit(200);
      if (filter === "featured") q = q.eq("featured", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as LookRow[];
    },
  });

  const toggleFeatured = useMutation({
    mutationFn: async ({ id, featured }: { id: string; featured: boolean }) => {
      const { error } = await supabase.from("looks").update({ featured }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-looks"] }),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-serif text-2xl">Looks</h2>
        <p className="mt-1 text-sm text-muted-foreground">Toggle which creator looks are featured across the site.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["all", "featured"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`border px-3 py-1.5 text-xs uppercase tracking-widest ${
              filter === s ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "all" ? "All" : "Featured only"}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">{list.data?.length ?? 0} shown</span>
      </div>

      <div className="overflow-x-auto border border-border bg-card -mx-4 sm:mx-0">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="px-4 py-2"></th>
              <th className="px-4 py-2">Title</th>
              <th className="px-4 py-2">Creator</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2 text-right">Featured</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            )}
            {!list.isLoading && (list.data?.length ?? 0) === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No looks found.</td></tr>
            )}
            {list.data?.map((l) => {
              const busy = toggleFeatured.isPending && toggleFeatured.variables?.id === l.id;
              return (
                <tr key={l.id} className="border-t border-border">
                  <td className="px-4 py-2">
                    <img src={publicUrl(l.image_path)} alt={l.title ?? "Look"} className="h-14 w-14 object-cover" />
                  </td>
                  <td className="px-4 py-2"><span className="line-clamp-1">{l.title ?? "Untitled look"}</span></td>
                  <td className="px-4 py-2 text-muted-foreground">{l.creators?.display_name ?? l.creators?.handle ?? "—"}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-2">
                      {busy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                      <Switch
                        checked={l.featured}
                        disabled={toggleFeatured.isPending}
                        onCheckedChange={(checked) => toggleFeatured.mutate({ id: l.id, featured: checked })}
                        aria-label={`Toggle featured for ${l.title ?? "look"}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {toggleFeatured.error && (
        <p className="text-xs text-rose-700">{(toggleFeatured.error as Error).message}</p>
      )}
    </div>
  );
}
